<template>
  <div class="sub-section">
    <b-row class="sub-section-header">
      <b-col cols="12" xl="7" class="p-0">
        <div>
          <a @click="$emit('hide')">
            <font-awesome-icon
              :icon="['fas', 'arrow-left']"
              size="lg"
              style="margin-bottom: 0.3rem;"
            />
          </a>
          &nbsp;&nbsp;

          <h3 style="display: inline-block;">{{ $t('account.acctDetails.addresses') }}</h3>
        </div>
        <hr class="d-block d-lg-none">
      </b-col>
      <!-- Add button for larger screens -->
      <b-col
        cols="12" xl="5"
        class="align-right p-0 d-none d-lg-block"
        v-if="!isShowAddAddress"
      >
        <b-btn class="alt-primary-button m-0" @click="showAddAddress()">
          <font-awesome-icon icon="plus"/>&nbsp;&nbsp;
          {{ $t('account.addresses.newAddr') }}
        </b-btn>
      </b-col>
      <!-- Add button for smaller screens -->
      <b-col class="d-block d-lg-none p-0 align-center" v-if="!isShowAddAddress">
        <a  @click="showAddAddress()">
          <font-awesome-icon icon="plus"/>&nbsp;&nbsp;
          {{ $t('account.addresses.newAddr') }}
        </a>
        <hr>
      </b-col>
    </b-row>

    <div v-if="allAddresses.length > 0 && !isShowAddAddress">
      <div class="address-list">
        <b-card
          v-for="(addr, aIndex) in allAddresses"
          v-bind:key="aIndex"
          class="col-12 col-lg-5 mt-4 px-3 py-4"
          no-body
        >
          <!-- Shows card header only if its default address -->
          <div class="default-address" v-if="addr._id === defaultAddressId">
            <h6 style="display: inline-block; font-size: 1.1rem;">
              {{ $t('account.addresses.defaultAddr') }}
            </h6>
            &nbsp;
            <font-awesome-icon :icon="['fas', 'dot-circle']" class="default-addr-icon"/>
            <hr class="mt-1">
          </div>

          <!-- Card body -->
          <b-col class="px-3">
            <b-row style="font-size: 1rem;">
              <b-col class="px-0">
                <b>{{addr.givenNames}} {{addr.lastNames}}</b>
                <br>
                {{addr.street}}
                <br>
                {{addr.district}}
                <br>
                {{addr.city}} {{addr.state}} {{addr.zipCode}}
                <br>
                {{ addr.country }}
                <br>
                {{ addr.mobilePhone }}
              </b-col>
            </b-row>
            <b-row class="align-right mt-4">
              <div >
                <b-btn variant="light" @click="editClicked(addr)">
                  {{ $t('edit') }}
                </b-btn>
                &nbsp;&nbsp;&nbsp;
                <b-btn variant="light" @click="deleteClicked(addr)">
                  {{ $t('delete') }}
                </b-btn>
              </div>
            </b-row>
            <!-- Show button to set default address only if its not default one -->
            <b-row class="align-right mt-2" v-if="addr._id !== defaultAddressId">
              <div>
                <b-btn variant="light" @click="setDefaultClicked(addr)">
                  {{ $t('account.addresses.setDefault') }}
                </b-btn>
              </div>
            </b-row>
          </b-col>
        </b-card>
      </div>
    </div>
    <div v-else-if="allAddresses.length <= 0 && !isShowAddAddress">
      <div class="empty-info">
        <p>{{ $t('account.addresses.emptyAddresses') }}</p>
      </div>
    </div>

    <!-- Input form to add new address for the user -->
    <transition
      name="address-form-anim"
      enter-active-class="animated fadeInRight faster"
      leave-active-class="animated fadeOutRight faster"
    >
      <div v-if="isShowAddAddress" class="address-form">
        <h5 class="mb-4">{{ $t('account.addresses.newAddr') }}</h5>
        <b-row>
          <b-col md="6">
            <b-form-group>
              <label for="givenNames">{{ $t('givenNames') }}</label>
              <b-form-input
                id="givenNames"
                type="text"
                name="givenNames"
                size="sm"
                :state="givenNamesState"
                v-model="shippingAddr.givenNames"
                :placeholder="$t('account.addresses.givenNamesHolder')"
                aria-describedby="givenNamesFeedB"
              ></b-form-input>
              <b-form-invalid-feedback id="givenNamesFeedB">
                <!-- This will only be shown if the preceeding input has an invalid state -->
                {{ $t('account.addresses.givenNamesFeedB') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group>
              <label for="lastNames">{{ $t('lastNames') }}</label>
              <b-form-input
                id="lastNames"
                size="sm"
                type="text"
                name="lastNames"
                :state="lastNamesState"
                v-model="shippingAddr.lastNames"
                :placeholder="$t('account.addresses.lastNamesHolder')"
                aria-describedby="lastNamesFeedB"
              ></b-form-input>
              <b-form-invalid-feedback id="lastNamesFeedB">
                <!-- This will only be shown if the preceeding input has an invalid state -->
                {{ $t('account.addresses.lastNamesFeedB') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" lg="6">
            <b-form-group>
              <label for="phoneNum">{{ $t('phoneNumber') }}</label>
              <b-form-input
                id="phoneNum"
                type="number"
                name="phoneNum"
                :state="phoneState"
                size="sm"
                v-model="shippingAddr.mobilePhone"
                :placeholder="$t('account.addresses.phoneNumHolder')"
                aria-describedby="phoneNumFeedB"
              ></b-form-input>
              <b-form-invalid-feedback id="phoneNumFeedB">
                <!-- This will only be shown if the preceeding input has an invalid state -->
                {{ $t('account.addresses.phoneNumFeedB') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group>
          <label for="street">{{ $t('account.addresses.street') }}</label>
          <b-form-input
            id="street"
            type="text"
            size="sm"
            name="street"
            :state="streetState"
            v-model="shippingAddr.street"
            :placeholder="$t('account.addresses.streetHolder')"
            aria-describedby="streetFeedB"
          ></b-form-input>
          <b-form-invalid-feedback id="streetFeedB">
            <!-- This will only be shown if the preceeding input has an invalid state -->
            {{ $t('account.addresses.streetFeedB') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-row>
          <b-col cols="12" lg="6">
            <b-form-group>
              <label for="country">{{ $t('account.addresses.country') }}</label>
              <b-form-select
                v-model="shippingAddr.country"
                :options="countryOptions"
                size="sm"
                :state="countryState"
                name="country"
                id="country"
              />
              <b-form-invalid-feedback id="countryFeedback">
                <!-- This will only be shown if the preceeding input has an invalid state -->
                {{ $t('emptyFieldFeedB') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12" lg="6">
            <b-form-group>
              <label for="zip">{{ $t('account.addresses.zipCode') }}</label>
              <b-form-input
                id="zip"
                type="number"
                name="zip"
                size="sm"
                :state="zipCodeState"
                v-model="shippingAddr.zipCode"
                :placeholder="$t('account.addresses.zipCodeHolder')"
                aria-describedby="zipCodeFeedback"
              ></b-form-input>
              <b-form-invalid-feedback id="zipCodeFeedback">
                <!-- This will only be shown if the preceeding input has an invalid state -->
                {{ $t('account.addresses.zipCodeFeedB') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" lg="6">
            <b-form-group>
              <label for="state">{{ $t('account.addresses.state') }}</label>
              <b-form-input
                id="state"
                type="text"
                name="state"
                size="sm"
                :state="stateState"
                v-model="shippingAddr.state"
                :placeholder="$t('account.addresses.stateHolder')"
                aria-describedby="stateFeedback"
              ></b-form-input>
              <b-form-invalid-feedback id="stateFeedback">
                <!-- This will only be shown if the preceeding input has an invalid state -->
                {{ $t('account.addresses.stateFeedB') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12" lg="6">
            <b-form-group>
              <label for="city">{{ $t('account.addresses.city') }}</label>
              <b-form-input
                id="city"
                type="text"
                name="city"
                :state="cityState"
                size="sm"
                v-model="shippingAddr.city"
                :placeholder="$t('account.addresses.cityHolder')"
                aria-describedby="cityStatee"
              ></b-form-input>
              <b-form-invalid-feedback id="cityStatee">
                <!-- This will only be shown if the preceeding input has an invalid state -->
                {{ $t('account.addresses.cityFeedB') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" lg="6">
            <b-form-group>
              <label for="district">{{ $t('account.addresses.district') }}</label>
              <b-form-input
                id="district"
                type="text"
                size="sm"
                name="district"
                :state="districtState"
                v-model="shippingAddr.district"
                :placeholder="$t('account.addresses.districtHolder')"
                aria-describedby="districtFeedB"
              ></b-form-input>
              <b-form-invalid-feedback id="districtFeedB">
                <!-- This will only be shown if the preceeding input has an invalid state -->
                {{ $t('account.addresses.districtFeedB') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="ml-3">
          <!-- Edit/Update button -->
          <b-button
            class="alt-primary-button"
            :disabled="!checkState"
            @click="updateAddress()"
            v-if="isUpdate"
          >
            {{ $t('edit')}}
          </b-button>

          <!-- Add button -->
          <b-button
            class="alt-primary-button"
            :disabled="!checkState"
            @click="saveAddress()"
            v-else
          >
            {{ $t('save') }}
          </b-button>

          <!-- Cancel button -->
          <b-button variant="secondary-button" class="acct-back-btn" @click="cancelForm()">
            {{ $t('cancel') }}
          </b-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import AccountMixin from '../../services/AccountMixin';
import ShippingAddrDTO from '@/dto/ShippingAddress.json';
import { mapGetters } from 'vuex';

export default {
  name: 'AddressBook',
  async created() {
    this.shippingAddr = _.cloneDeep(ShippingAddrDTO);
  },

  data() {
    return {
      isShowAddAddress: false,
      shippingAddr: null,
      description: '',
      isUpdate: false,
      countryOptions: ['United States of America', 'México'],
    };
  },

  mixins: [AccountMixin],

  computed: {
    ...mapGetters({
      allAddresses: 'accountStore/getAddresses',
      defaultAddressId: 'accountStore/getDefaultAddressId',
      isSessionActive: 'authStore/isSessionActive',
    }),

    givenNamesState() {
      return this.getGivenNamesState(this.shippingAddr.givenNames);
    },

    lastNamesState() {
      return this.getLastNamesState(this.shippingAddr.lastNames);
    },

    phoneState() {
      return this.getPhoneState(this.shippingAddr.mobilePhone);
    },

    streetState() {
      if (this.shippingAddr.street.length === 0) return null;
      return this.validStreet(this.shippingAddr.street);
    },

    countryState() {
      if (this.shippingAddr.country.length === 0) return null;
      return this.validCountry(this.shippingAddr.country);
    },

    stateState() {
      if (this.shippingAddr.state.length === 0) return null;
      return this.validState(this.shippingAddr.state);
    },

    cityState() {
      if (this.shippingAddr.city.length === 0) return null;
      return this.validCity(this.shippingAddr.city);
    },

    zipCodeState() {
      if (this.shippingAddr.zipCode.length === 0) return null;
      return this.validZipCode(this.shippingAddr.zipCode);
    },

    districtState() {
      if (this.shippingAddr.district.length === 0) return null;
      return this.validDistrict(this.shippingAddr.district);
    },


    checkState() {
      return (
        this.givenNamesState
        && this.streetState
        && this.stateState
        && this.zipCodeState
        && this.districtState
        && this.countryState
        && this.cityState
      );
    }
  },

  methods: {
    showAddAddress() {
      this.isShowAddAddress = true;
    },
    hideAddAddress() {
      this.isShowAddAddress = false;
    },

    validStreet(street) {
      const streetRegex = /^[\p{L}\s\d#.,]{1,90}$/u;
      return streetRegex.test(street);
    },

    validCity(city) {
      const regex = /^[\p{L}\s]{1,80}$/u;
      return regex.test(city);
    },

    validState(state) {
      const regex = /^[\p{L}\s]{1,60}$/u;
      return regex.test(state);
    },

    validCountry(country) {
      const regex = /^[\p{L}\s]{1,50}$/u;
      return regex.test(country);
    },

    validZipCode(zipCode) {
      const regex = /^[0-9]{5}$/u;
      return regex.test(zipCode);
    },

    validDistrict(district) {
      const streetRegex = /^[\p{L}\s\d.,]{1,60}$/u;
      return streetRegex.test(district);
    },

    cancelForm() {
      this.resetFields();
      this.isShowAddAddress = false;
      this.isUpdate = false;
    },

    resetFields() {
      // eslint-disable-next-line
      for (const key in this.shippingAddr) {
        this.shippingAddr[key] = '';
      }
    },

    sanitizeFields() {
      for(const key in this.shippingAddr) {
        this.shippingAddr[key] = this.shippingAddr[key].trim();
      }
    },

    addressEqual(address) {
      return _.isEqual(address._id, this.defaultAddressId);
    },

    chooseAddress(add) {
      this.$emit('selected', add);
    },

    editClicked(address) {
      this.isUpdate = true;
      this.shippingAddr = _.cloneDeep(address);
      this.isShowAddAddress = true;
    },

    async deleteClicked(address) {
      try {
        await this.$store.dispatch('accountStore/deleteAddress', { addressId: address._id });
        this.$notify({
          group: 'all',
          type: 'success',
          text: this.$t('account.addresses.deleteOk'),
        });
      } catch (err) {
        console.log('Error', err);
        this.$notify({
          group: 'all',
          type: 'error',
          text: this.$t('account.addresses.deleteErr'),
        });
      }
    },

    async setDefaultClicked(address) {
      try {
        await this.$store.dispatch(
          'accountStore/setDefaultAddress',
          { addressId: address._id }
        );

        this.$notify({
          group: 'all',
          type: 'success',
          text: this.$t('account.addresses.setDefaultOk'),
        });
      } catch (err) {
        console.log('Error', err);
        this.$notify({
          group: 'all',
          type: 'error',
          text: this.$t('account.addresses.setDefaultErr'),
        });
      }
    },

    async saveAddress() {
      try {
        this.sanitizeFields();
        if(this.checkState) {
          await this.$store.dispatch('accountStore/addNewAddress', _.cloneDeep(this.shippingAddr));

          this.resetFields();
          this.$notify({
            group: 'all',
            type: 'success',
            text: this.$t('account.addresses.saveOk'),
          });
          
          // Hide form
          this.isShowAddAddress = false;
          this.isUpdate = false;
        }
        else return;
      }
      catch (err) {
        console.log('Error', err);
        this.$notify({
          group: 'all',
          type: 'error',
          text: this.$t('account.addresses.saveErr'),
        });
      }
    },

    async updateAddress() {
      try {
        this.sanitizeFields();
        if(this.checkState) {
          await this.$store.dispatch('accountStore/updateAddress', _.cloneDeep(this.shippingAddr));

          this.resetFields();
          this.$notify({
            group: 'all',
            type: 'success',
            text: this.$t('account.addresses.updateOk'),
          });

          // Hide form
          this.isShowAddAddress = false;
          this.isUpdate = false;
        }
        else return;
      }
      catch (err) {
        console.log('Error', err);
        this.$notify({
          group: 'all',
          type: 'error',
          text: this.$t('account.addresses.updateErr'),
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
  @import '../../assets/css/constants.scss';

  .address-list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-around;

    padding: 0px;
    margin-bottom: 1rem;

    button {
      border-color: #dbdbdb !important;
    }
  }

  .default-addr-icon {
    color: $secondary-hover-textColor;
  }
  
  .address-form {
    @media (min-width: 992px) {
      padding: 0 1rem;
    }
    // background: white;

    .cancel-btn {
      margin-right: 10px;
      margin-top: 2em;
    }

    .action-buttons {
      padding: 10px 0px;
    }
  }
</style>